import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { Pros } from 'src/providers/pros/pros';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Addresses } from 'src/providers/customer/addresses';

import { Util } from 'src/shared/util/util';

import { Loading } from 'src/shared/components/loading/loading';

import {
  JobCredentialRequirementsApiService,
  CredentialTypeMetadata,
  JobCredentialRequirement,
  SetRequirementPayload
} from 'src/shared/services/job-credential-requirements-api.service';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import { ClientSettingsModel } from 'src/models/client.model';
import { TidySelectStringValueModel } from 'src/models/tidy-select-item.model';
import { TidyCurrencyPipe } from 'src/shared/pipes/tidy-currency.pipe';
import { EditPricesPage } from 'src/pages/more/edit-prices/edit-prices';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    templateUrl: 'tidy-find-new-pro.html',
    standalone: false
})
export class TidyFindNewProPage implements OnInit {

  private featureGate = inject(FeatureGateService);

  isLoadingForm: boolean;
  isRentalClient: boolean;
  selectedCategory: any;
  errorMessage: string;
  allowsFindNewPros: boolean;
  customerId: number;
  form: UntypedFormGroup;
  genderRequired = false;
  genderOptions: TidySelectStringValueModel[];
  hasPrivatePro: boolean;
  initialFindNewProsValue: boolean;
  serviceCategoryItems: any;
  settings: ClientSettingsModel;
  updatedSettings: {homekeeper_preferences: {gender: string}} = {homekeeper_preferences: {gender: ''}};
  loaded: boolean;
  isRightSideContent: boolean;
  dialogParams: any;
  addressList: any[];
  selectedAddressId: any;
  pricingForm: UntypedFormGroup;
  services: any;
  frequencies = [
    { value: 'every_week', viewValue: new TranslationPipe().transform('Recurring') },
    { value: 'once', viewValue: new TranslationPipe().transform('One Time') },
    { value: 'same_day_cleaning', viewValue: new TranslationPipe().transform('Same Day') }
  ];
  billingPreference = [
    { value: 'charge_before', viewValue: new TranslationPipe().transform('Charge Before')},
    { value: 'charge_after', viewValue: new TranslationPipe().transform('Charge After')},
  ];
  // Insurance requirement with sub-option for liability
  insuranceRequired = false;

  // Background check with sub-options
  backgroundCheckRequired = false;
  backgroundCheckType = 'certification'; // 'certification' or 'custom'
  backgroundCheckOptions = [
    { value: 'certification', viewValue: 'Background Check Certification' },
    { value: 'custom', viewValue: 'Custom Background Check' }
  ];

  // Compliance items (hasDetails: true means show text input when checked)
  complianceItems: any[] = [
    { value: 'Training/Certification', viewValue: 'Training/Certification', checked: false, hasDetails: true, details: '' },
    { value: 'Document signing (e.g. NDA)', viewValue: 'Document signing (e.g. NDA)', checked: false, hasDetails: true, details: '' },
    { value: 'Licenses', viewValue: 'Licenses', checked: false, hasDetails: true, details: '' },
    { value: 'Language', viewValue: 'Language', checked: false, hasDetails: true, details: '' },
    { value: 'Identification Certification', viewValue: 'Identification Certification', checked: false, hasDetails: false },
    { value: 'Quality Certification', viewValue: 'Quality Certification', checked: false, hasDetails: false },
    { value: 'Reliability Certification', viewValue: 'Reliability Certification', checked: false, hasDetails: false },
  ];

  // Equipment requirements with sub-options
  equipmentRequired = false;
  equipmentSubOptions: any[] = [
    { value: 'ecoFriendly', viewValue: 'Eco-Friendly', checked: false },
    { value: 'equipmentCertification', viewValue: 'Equipment Certification', checked: false },
    { value: 'equipmentOther', viewValue: 'Other', checked: false, hasDetails: true, details: '' }
  ];

  // Non-compliant handling options
  nonCompliantHandling = 'remove';
  nonCompliantOptions = [
    { value: 'remove', viewValue: 'Remove non-compliant pros automatically' },
    { value: 'flag', viewValue: 'Flag non-compliant pros for review' },
    { value: 'none', viewValue: 'Take no action' }
  ];
  sourceNetworks = [
    { value: 'Yelp', viewValue: 'Yelp', checked: false },
    { value: 'Thumbtack', viewValue: 'Thumbtack', checked: false },
    { value: 'Angi', viewValue: 'Angi', checked: false },
    { value: 'HomeAdvisor', viewValue: 'HomeAdvisor', checked: false },
    { value: 'Nextdoor', viewValue: 'Nextdoor', checked: false },
    { value: 'Facebook', viewValue: 'Facebook', checked: false },
    { value: 'TIDY', viewValue: 'TIDY', checked: false }
  ];

  // Budget preference options
  budgetPreferenceOptions = [
    { value: 'budget', viewValue: 'Budget Conscious - Affordable pros, flexible requirements' },
    { value: 'market', viewValue: 'Market Rate - Balanced quality and value' },
    { value: 'premium', viewValue: 'Premium - Top-tier pros, best quality' }
  ];

  // Settings form
  settingsForm: UntypedFormGroup;

  // Experience items (moved to complianceItems)
  experienceItems: any[] = [];

  // Availability items
  availabilityItems = [
    { value: 'sameDay', viewValue: 'Same-Day Availability Required', checked: false },
    { value: 'weekends', viewValue: 'Weekend Availability', checked: true },
    { value: 'holidays', viewValue: 'Holiday Availability', checked: false }
  ];

  timeSlotItems = [
    { value: 'morning', viewValue: 'Morning (6am-12pm)', checked: true },
    { value: 'afternoon', viewValue: 'Afternoon (12pm-5pm)', checked: true },
    { value: 'evening', viewValue: 'Evening (5pm-9pm)', checked: false }
  ];

  // Communication items
  languageItems = [
    { value: 'english', viewValue: 'English', checked: true },
    { value: 'spanish', viewValue: 'Spanish', checked: false }
  ];

  communicationItems = [
    { value: 'photoDocumentation', viewValue: 'Must Provide Before/After Photos', checked: false },
    { value: 'realTimeUpdates', viewValue: 'Real-Time Updates Required', checked: false }
  ];

  // Trust & safety items (removed - Drug Testing and Bonded replaced by "Other" in compliance)
  trustSafetyItems: any[] = [];

  // Workers Comp (shown when insurance is required)
  workersCompRequired = false;

  // Select options
  ratingOptions = [
    { value: 'any', viewValue: 'Any Rating' },
    { value: '3.0', viewValue: '3.0+ Stars' },
    { value: '3.5', viewValue: '3.5+ Stars' },
    { value: '4.0', viewValue: '4.0+ Stars' },
    { value: '4.5', viewValue: '4.5+ Stars' }
  ];

  reviewCountOptions = [
    { value: 'any', viewValue: 'Any' },
    { value: '1', viewValue: '1+ Reviews' },
    { value: '5', viewValue: '5+ Reviews' },
    { value: '10', viewValue: '10+ Reviews' },
    { value: '25', viewValue: '25+ Reviews' },
    { value: '50', viewValue: '50+ Reviews' }
  ];

  yearsExperienceOptions = [
    { value: 'any', viewValue: 'Any' },
    { value: '1', viewValue: '1+ Years' },
    { value: '2', viewValue: '2+ Years' },
    { value: '5', viewValue: '5+ Years' }
  ];

  searchRadiusOptions = [
    { value: 'any', viewValue: 'Any Distance' },
    { value: '10', viewValue: '10 Miles' },
    { value: '25', viewValue: '25 Miles' },
    { value: '50', viewValue: '50 Miles' }
  ];

  autoAcceptOptions = [
    { value: 'manual', viewValue: 'Manual Review Only' },
    { value: '90', viewValue: 'Auto-Accept 90%+ Match' },
    { value: '80', viewValue: 'Auto-Accept 80%+ Match' },
    { value: '70', viewValue: 'Auto-Accept 70%+ Match' }
  ];

  liabilityOptions = [
    { value: 'any', viewValue: 'Any' },
    { value: '500000', viewValue: '$500K Minimum' },
    { value: '1000000', viewValue: '$1M Minimum' },
    { value: '2000000', viewValue: '$2M Minimum' }
  ];

  private readonly SECTION_SEPARATOR = ';;';

  allCredentialTypes: CredentialTypeMetadata[] = [];
  activeRequirements: JobCredentialRequirement[] = [];
  backendReviewSources: { key: string; label: string; checked: boolean; strictness: string }[] = [];
  backendComplianceItems: { key: string; label: string; checked: boolean; strictness: string }[] = [];

  boundSave = this.save.bind(this);

constructor(
    private client: Client,
    private pros: Pros,
    private fb: UntypedFormBuilder,
    private util: Util,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private addresses: Addresses,
    private credentialApi: JobCredentialRequirementsApiService,
    private cdr: ChangeDetectorRef
  ) {
    this.pricingForm = this.fb.group({
      frequency: ['every_week', Validators.required],
      chargeType: ['charge_before', Validators.required]
    });
    this.form = this.fb.group({
      gender: ['any', Validators.required]
    });
    this.settingsForm = this.fb.group({
      budgetPreference: ['market'],
      negativeKeywords: [''],
      minRating: ['4.0'],
      minReviews: ['10'],
      yearsExperience: ['2'],
      searchRadius: ['any'],
      autoAccept: ['manual'],
      liabilityMinimum: ['any']
    });
  }

  @Loading('', true)
  async ngOnInit() {
    try {
      this.loaded = false;
      const categories = await this.pros.getServiceCategoriesOrFetch();
      this.serviceCategoryItems = this.getServiceCategoryItems(categories);
      this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      this.hasPrivatePro = await this.pros.checkIfHasPrivatePro();
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      this.rightSidePanelService.setDialogPageTitle('Find New Pro Settings');
      if (this.isRightSideContent) {
        this.dialogParams = await this.storage.retrieve('dialog-params');
      }
      const passedCategoryId = this.dialogParams?.categoryId || 1;
      this.selectedCategory = this.serviceCategoryItems.find(item => item.value === passedCategoryId);
      localStorage.setItem('selectedCategoryId', this.selectedCategory.value.toString());
      this.allowsFindNewPros = await this.checkIfAllowsFindNewPros();
      this.initialFindNewProsValue = this.allowsFindNewPros;
      await this.loadAddresses();
      this.selectedAddressId = parseInt(localStorage.getItem('addressId'));
      this.settings = await this.client.getClientSettings();
      const clientInfo = await this.client.getClientInfo();
      this.customerId = clientInfo?.customer_account?.id;
      await this.loadCredentialData();
      if (this.selectedAddressId) {
        await this.loadPricesForAddress();
      }
      this.populateFields();
      await this.getMetadataAndPatchForm();
      this.loaded = true;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  async checkIfAllowsFindNewPros() {
    const prosData = await this.pros.getPros(this.selectedCategory.value);
    const isBlocked = prosData.blocked.find((item) => item.object_type === 'dynamic_sa')
    return !isBlocked;
  }

  async getMetadataAndPatchForm() {
    const settingsKey = `external.findNewProSettings${this.selectedAddressId}`;
    const settingsResponse = await this.client.getMetadata([settingsKey]);
    const combinedValue = this.extractMetadataValue(settingsResponse, settingsKey);

    if (combinedValue && combinedValue !== 'null') {
      const sections = this.parseCombinedSettings(combinedValue);
      this.applyRequirementsSection(sections.requirements);
      this.applyCertificationsSection(sections.certifications);
      this.applySourcesSection(sections.sources);
      this.applyTemplateSection(sections.template);
      this.applyQualitySection(sections.quality);
      this.applyExperienceSection(sections.experience);
      this.applyAvailabilitySection(sections.availability);
      this.applyCommunicationSection(sections.communication);
      this.applyTrustSafetySection(sections.trustSafety);
      this.applyAdvancedSection(sections.advanced);
    } else {
      await this.getMetadataAndPatchFormLegacy();
    }
  }

  private parseCombinedSettings(combinedValue: string): Record<string, string> {
    const sectionNames = [
      'requirements', 'certifications', 'sources', 'template', 'quality',
      'experience', 'availability', 'communication', 'trustSafety', 'advanced'
    ];
    const sections = combinedValue.split(this.SECTION_SEPARATOR);
    const result: Record<string, string> = {};
    sectionNames.forEach((name, index) => {
      result[name] = sections[index] || '';
    });
    return result;
  }

  private applyRequirementsSection(value: string) {
    if (!value || value === 'null') { return; }
    const requirementPairs = value.split(',').filter(r => r);
    requirementPairs.forEach(pair => {
      if (pair.includes('Insurance')) {
        this.insuranceRequired = true;
      }
      if (pair.includes('Background check')) {
        this.backgroundCheckRequired = true;
        if (pair.includes('custom')) {
          this.backgroundCheckType = 'custom';
        }
      }
      if (pair.startsWith('Equipment(')) {
        this.equipmentRequired = true;
        const optionsMatch = pair.match(/Equipment\(([^)]*)\)/);
        if (optionsMatch && optionsMatch[1]) {
          const options = optionsMatch[1].split('|');
          options.forEach(opt => {
            const [optValue, details] = opt.split(':');
            const subOption = this.equipmentSubOptions.find(o => o.value === optValue);
            if (subOption) {
              subOption.checked = true;
              if (details && subOption.hasDetails) {
                subOption.details = details;
              }
            }
          });
        }
      }
    });
  }

  private applyCertificationsSection(value: string) {
    if (!value || value === 'null') { return; }
    this.complianceItems.map(item => {
      item.checked = false;
    });
    const certificationsList = value.split(',').filter(c => c);
    this.complianceItems = this.complianceItems.map(item => {
      const updatedItem = { ...item };
      const matchingCert = certificationsList.find(certName => {
        const certValue = certName.split('(')[0].trim();
        return certValue === updatedItem.value;
      });
      if (matchingCert) {
        updatedItem.checked = true;
        if (updatedItem.hasDetails) {
          const detailsMatch = matchingCert.match(/\(([^)]+)\)$/);
          if (detailsMatch) {
            updatedItem.details = detailsMatch[1];
          }
        }
      }
      return updatedItem;
    });
    const nonCompliantMatch = certificationsList.find(c => c.includes('nonCompliantHandling'));
    if (nonCompliantMatch) {
      const match = nonCompliantMatch.match(/nonCompliantHandling\(([^)]+)\)/);
      if (match) {
        this.nonCompliantHandling = match[1];
      }
    }
  }

  private applySourcesSection(value: string) {
    if (!value || value === 'null') { return; }
    this.sourceNetworks.map(item => {
      item.checked = false;
    });
    const sourcesList = value.split(',').filter(s => s);
    this.sourceNetworks = this.sourceNetworks.map(item => {
      const updatedItem = { ...item };
      updatedItem.checked = sourcesList.some(sourceName => sourceName.trim() === updatedItem.value);
      return updatedItem;
    });
  }

  private applyTemplateSection(value: string) {
    if (!value || value === 'null' || value === 'custom') { return; }
    this.settingsForm.patchValue({ budgetPreference: value });
  }

  private applyQualitySection(value: string) {
    if (!value || value === 'null') { return; }
    this.parseKeyValueString(value, (key, val) => {
      if (key === 'minRating') { this.settingsForm.patchValue({ minRating: val }); }
      if (key === 'minReviews') { this.settingsForm.patchValue({ minReviews: val }); }
    });
  }

  private applyExperienceSection(value: string) {
    if (!value || value === 'null') { return; }
    this.parseKeyValueString(value, (key, val) => {
      if (key === 'yearsExperience') { this.settingsForm.patchValue({ yearsExperience: val }); }
      if (key === 'ownsEquipment') {
        const item = this.experienceItems.find(i => i.value === 'ownsEquipment');
        if (item) { item.checked = val === 'true'; }
      }
      if (key === 'ecoFriendly') {
        const item = this.experienceItems.find(i => i.value === 'ecoFriendly');
        if (item) { item.checked = val === 'true'; }
      }
    });
  }

  private applyAvailabilitySection(value: string) {
    if (!value || value === 'null') { return; }
    this.availabilityItems.forEach(item => item.checked = false);
    this.timeSlotItems.forEach(item => item.checked = false);
    this.parseKeyValueString(value, (key, val) => {
      if (key === 'sameDay' || key === 'weekends' || key === 'holidays') {
        const item = this.availabilityItems.find(i => i.value === key);
        if (item) { item.checked = val === 'true'; }
      }
      if (key === 'timeSlots') {
        const slots = val.split('|');
        this.timeSlotItems.forEach(item => {
          item.checked = slots.includes(item.value);
        });
      }
    });
  }

  private applyCommunicationSection(value: string) {
    if (!value || value === 'null') { return; }
    this.languageItems.forEach(item => item.checked = false);
    this.communicationItems.forEach(item => item.checked = false);
    this.parseKeyValueString(value, (key, val) => {
      if (key === 'languages') {
        const langs = val.split('|');
        this.languageItems.forEach(item => {
          item.checked = langs.includes(item.value);
        });
      }
      if (key === 'photoDocumentation' || key === 'realTimeUpdates') {
        const item = this.communicationItems.find(i => i.value === key);
        if (item) { item.checked = val === 'true'; }
      }
    });
  }

  private applyTrustSafetySection(value: string) {
    if (!value || value === 'null') { return; }
    this.trustSafetyItems.forEach(item => item.checked = false);
    this.parseKeyValueString(value, (key, val) => {
      if (key === 'liabilityMinimum') { this.settingsForm.patchValue({ liabilityMinimum: val }); }
      if (key === 'workersComp') { this.workersCompRequired = val === 'true'; }
      const item = this.trustSafetyItems.find(i => i.value === key);
      if (item) {
        if (val === 'true' || val === 'false') {
          item.checked = val === 'true';
        } else {
          item.checked = true;
          if (item.hasDetails) {
            item.details = val;
          }
        }
      }
    });
  }

  private applyAdvancedSection(value: string) {
    if (!value || value === 'null') { return; }
    this.parseKeyValueString(value, (key, val) => {
      if (key === 'searchRadius') { this.settingsForm.patchValue({ searchRadius: val }); }
      if (key === 'autoAccept') { this.settingsForm.patchValue({ autoAccept: val }); }
      if (key === 'negativeKeywords') { this.settingsForm.patchValue({ negativeKeywords: val }); }
    });
  }

  // Legacy fallback for users with data under old 10 separate keys
  private async getMetadataAndPatchFormLegacy() {
    const requirementsKey = `external.findNewProsRequirements${this.selectedAddressId}`;
    const certificationsKey = `external.findNewProsCertifications${this.selectedAddressId}`;
    const sourcesKey = `external.findNewProsSources${this.selectedAddressId}`;
    const templateKey = `external.findNewProsTemplate${this.selectedAddressId}`;
    const qualityKey = `external.findNewProsQuality${this.selectedAddressId}`;
    const experienceKey = `external.findNewProsExperience${this.selectedAddressId}`;
    const availabilityKey = `external.findNewProsAvailability${this.selectedAddressId}`;
    const communicationKey = `external.findNewProsCommunication${this.selectedAddressId}`;
    const trustSafetyKey = `external.findNewProsTrustSafety${this.selectedAddressId}`;
    const advancedKey = `external.findNewProsAdvanced${this.selectedAddressId}`;

    const [
      requirementsResponse,
      certificationsResponse,
      sourcesResponse,
      templateResponse,
      qualityResponse,
      experienceResponse,
      availabilityResponse,
      communicationResponse,
      trustSafetyResponse,
      advancedResponse
    ] = await Promise.all([
      this.client.getMetadata([requirementsKey]),
      this.client.getMetadata([certificationsKey]),
      this.client.getMetadata([sourcesKey]),
      this.client.getMetadata([templateKey]),
      this.client.getMetadata([qualityKey]),
      this.client.getMetadata([experienceKey]),
      this.client.getMetadata([availabilityKey]),
      this.client.getMetadata([communicationKey]),
      this.client.getMetadata([trustSafetyKey]),
      this.client.getMetadata([advancedKey])
    ]);

    this.applyTemplateSection(this.extractMetadataValue(templateResponse, templateKey));
    this.applyQualitySection(this.extractMetadataValue(qualityResponse, qualityKey));
    this.applyExperienceSection(this.extractMetadataValue(experienceResponse, experienceKey));
    this.applyAvailabilitySection(this.extractMetadataValue(availabilityResponse, availabilityKey));
    this.applyCommunicationSection(this.extractMetadataValue(communicationResponse, communicationKey));
    this.applyTrustSafetySection(this.extractMetadataValue(trustSafetyResponse, trustSafetyKey));
    this.applyAdvancedSection(this.extractMetadataValue(advancedResponse, advancedKey));

    if (certificationsResponse.length > 0) {
      this.complianceItems.map(item => {
        item.checked = false;
      });
    }
    if (sourcesResponse.length > 0) {
      this.sourceNetworks.map(item => {
        item.checked = false;
      });
    }

    this.applyRequirementsSection(this.extractMetadataValue(requirementsResponse, requirementsKey));
    this.applyCertificationsSection(this.extractMetadataValue(certificationsResponse, certificationsKey));
    this.applySourcesSection(this.extractMetadataValue(sourcesResponse, sourcesKey));
  }

  extractMetadataValue(response: any, key: string): string | null {
    if (!response) {return null;}
    if (Array.isArray(response) && response[0]) {
      return response[0].value;
    } else if (response[key]) {
      return response[key];
    } else if (response.value) {
      return response.value;
    }
    return null;
  }

  parseKeyValueString(str: string, callback: (key: string, value: string) => void) {
    const pairs = str.split(',').filter(p => p);
    pairs.forEach(pair => {
      const match = pair.match(/^([^(]+)\(([^)]*)\)$/);
      if (match) {
        callback(match[1].trim(), match[2]);
      }
    });
  }

  saveMetadata() {
    // Build requirements string (insurance, background check, equipment)
    const requirementParts: string[] = [];
    if (this.insuranceRequired) {
      requirementParts.push('Insurance');
    }
    if (this.backgroundCheckRequired) {
      requirementParts.push(`Background check(${this.backgroundCheckType})`);
    }
    if (this.equipmentRequired) {
      const equipmentOptions = this.equipmentSubOptions
        .filter(opt => opt.checked)
        .map(opt => opt.hasDetails && opt.details ? `${opt.value}:${opt.details}` : opt.value)
        .join('|');
      requirementParts.push(`Equipment(${equipmentOptions})`);
    }
    const requirementsString = requirementParts.join(',');

    // Build certifications/compliance string (include details if present)
    const checkedCompliance = this.complianceItems
      .filter(item => item.checked)
      .map(item => item.hasDetails && item.details ? `${item.value}(${item.details})` : item.value);
    checkedCompliance.push(`nonCompliantHandling(${this.nonCompliantHandling})`);
    const certificationsString = checkedCompliance.join(',');
    const sourcesString = this.sourceNetworks
      .filter(source => source.checked)
      .map(source => source.value)
      .join(',');

    // Build quality string
    const qualityString = [
      `minRating(${this.settingsForm.value.minRating})`,
      `minReviews(${this.settingsForm.value.minReviews})`
    ].join(',');

    // Build experience string
    const experienceParts = [
      `yearsExperience(${this.settingsForm.value.yearsExperience})`
    ];
    this.experienceItems.forEach(item => {
      experienceParts.push(`${item.value}(${item.checked})`);
    });
    const experienceString = experienceParts.join(',');

    // Build availability string
    const availabilityParts: string[] = [];
    this.availabilityItems.forEach(item => {
      availabilityParts.push(`${item.value}(${item.checked})`);
    });
    const checkedTimeSlots = this.timeSlotItems.filter(t => t.checked).map(t => t.value).join('|');
    availabilityParts.push(`timeSlots(${checkedTimeSlots})`);
    const availabilityString = availabilityParts.join(',');

    // Build communication string
    const communicationParts: string[] = [];
    const checkedLanguages = this.languageItems.filter(l => l.checked).map(l => l.value).join('|');
    communicationParts.push(`languages(${checkedLanguages})`);
    this.communicationItems.forEach(item => {
      communicationParts.push(`${item.value}(${item.checked})`);
    });
    const communicationString = communicationParts.join(',');

    // Build trust & safety string (include details if present)
    const trustSafetyParts = [
      `liabilityMinimum(${this.settingsForm.value.liabilityMinimum})`,
      `workersComp(${this.workersCompRequired})`
    ];
    this.trustSafetyItems.forEach(item => {
      if (item.checked && item.hasDetails && item.details) {
        trustSafetyParts.push(`${item.value}(${item.details})`);
      } else {
        trustSafetyParts.push(`${item.value}(${item.checked})`);
      }
    });
    const trustSafetyString = trustSafetyParts.join(',');

    // Build advanced string
    const advancedString = [
      `searchRadius(${this.settingsForm.value.searchRadius})`,
      `autoAccept(${this.settingsForm.value.autoAccept})`,
      `negativeKeywords(${this.settingsForm.value.negativeKeywords || ''})`
    ].join(',');

    // Combine all sections into single value
    const combinedValue = [
      requirementsString,
      certificationsString,
      sourcesString,
      this.settingsForm.value.budgetPreference,
      qualityString,
      experienceString,
      availabilityString,
      communicationString,
      trustSafetyString,
      advancedString
    ].join(this.SECTION_SEPARATOR);

    return this.client.addMetadata({
      key: `findNewProSettings${this.selectedAddressId}`,
      value: combinedValue
    });
  }

  getServiceCategoryItems(serviceCategories) {
    const array: any = [];
    serviceCategories.map((service) => {
      array.push({
        viewValue: service.name,
        value: service.id,
        key: service.key
      });
    });
    return array;
  }

  async save() {
    this.errorMessage = '';
    try {
      if (this.allowsFindNewPros !== this.initialFindNewProsValue) {
         if (!this.hasPrivatePro && !this.allowsFindNewPros) {
          return this.goToUnableToBlock();
        }
        await this.pros.moveProToCollection(this.selectedAddressId.toString(), this.selectedCategory.value, this.allowsFindNewPros);
      }
      this.updatedSettings.homekeeper_preferences.gender = this.form.value.gender;
      await this.client.saveClientSettings(this.updatedSettings);
      await this.saveCredentialRequirements();
      await this.saveMetadata();
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.rightSidePanelService.navigateTo('job-request-workflows');
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  goToUnableToBlock() {
    const message = 'You must have at least 1 private pro on your list to stop allowing Find New Pro.';
    this.util.showError(message, 10000);
    this.rightSidePanelService.navigateTo('job-request-workflows');
  }

  populateFields() {
    this.genderOptions = [
      {
        viewValue: new TranslationPipe().transform('Best Available'),
        value: 'any'
      },
      {
        viewValue: new TranslationPipe().transform('Male Only'),
        value: 'male'
      },
      {
        viewValue: new TranslationPipe().transform('Female Only'),
        value: 'female'
      }
    ];
    const savedGender = this.settings.homekeeper_preferences.gender || 'any';
    this.form = this.fb.group({
      gender: [savedGender, Validators.required]
    });
  }

  async requestCategory() {
    try {
      const payload = {
        key: `find-new-pro/${  this.selectedCategory.value}`
      }
      await this.client.requestUpcomingFeature(payload);
      const message = 'Feature requested! TIDY will let you know when this feature is available for you.';
      this.util.showSuccess(message)
      this.rightSidePanelService.navigateTo('job-request-workflows');
    } catch(err) {
      if (err.message === 'You have already voted on this feature!') {
        const message = 'Feature alredy requested. TIDY will let you know when this feature is available for you.';
        this.util.showSuccess(message)
        this.rightSidePanelService.navigateTo('job-request-workflows');
      } else {
        this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      }
    }
  }

  goToBookDemo() {
    this.util.openUrl('https://www.tidy.com/demo');
  }

  async loadAddresses() {
    const addresses = await this.client.getAddresses();
    this.addressList = addresses.map(address => {
      const displayName = address?.address_name || `${address.address1} ${address.address2 ? `${address.address2  } ` : ''}${address.city}, ${address.zip}`;
      return {
        value: address.id,
        viewValue: displayName
      };
    });
  }

  @Loading('', true)
  async changeProperty(id) {
    try {
      this.isLoadingForm = true;
      this.selectedAddressId = id;
      this.allowsFindNewPros = await this.checkIfAllowsFindNewPros();
      this.initialFindNewProsValue = this.allowsFindNewPros;
      await this.loadCredentialData();
      await this.loadPricesForAddress();
      await this.getMetadataAndPatchForm();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    } finally {
      this.isLoadingForm = false;
      this.cdr.detectChanges();
    }
  }

  async changeServiceCategory(id) {
    try {
      this.isLoadingForm = true;
      this.selectedCategory = this.serviceCategoryItems.find(item => item.value === id);
      localStorage.setItem('selectedCategoryId', id.toString());
      this.allowsFindNewPros = await this.checkIfAllowsFindNewPros();
      this.initialFindNewProsValue = this.allowsFindNewPros;
      await this.loadPricesForAddress();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    } finally {
      this.isLoadingForm = false;
      this.cdr.detectChanges();
    }
  }

  async loadPricesForAddress() {
    if (this.selectedAddressId) {
      this.services = await this.addresses.getPriceTable(this.selectedAddressId);
      this.services = this.services.filter(service => service.key.includes(this.selectedCategory.key));
    }
  }

  getPrice(service) {
    let price = service.prices.base_price;
    if (this.pricingForm.value.frequency === 'once') {
      price = this.addPercent(price, service.optional_price_additional_fees.one_time_job.value);
    }
    if (this.pricingForm.value.frequency === 'same_day_cleaning') {
      price = this.addPercent(price, service.optional_price_additional_fees.same_day_job.value);
    }
    if (this.pricingForm.value.chargeType === 'charge_after') {
      price = this.addPercent(price, service.optional_price_additional_fees.charge_after?.value || 10);
    }
    return (price === 0 || price === null) ? 'Priced Later' : new TidyCurrencyPipe().transform(price);
  }

  addPercent(amount, percent) {
    amount /= 100;
    amount = (amount + (amount * (percent / 100)));
    return Math.round(amount) * 100;
  }

  goToEditPricesPage() {
    const params = {
      services: this.services,
      addressList: this.addressList,
      addressId: this.selectedAddressId
    };
    this.rightSidePanelService.navigateTo('edit-prices', params, EditPricesPage);
  }

  openCertificationHelp() {
    this.util.openUrl('https://help.tidy.com/certifications-and-ratings');
  }

  private async loadCredentialData(): Promise<void> {
    try {
      const [types, requirements] = await Promise.all([
        firstValueFrom(this.credentialApi.getCredentialTypes()),
        firstValueFrom(this.credentialApi.getRequirements('address', this.selectedAddressId))
      ]);
      this.allCredentialTypes = types;
      this.activeRequirements = requirements;
      this.buildCredentialCheckboxes();
    } catch {
      this.allCredentialTypes = [];
      this.activeRequirements = [];
      this.backendReviewSources = [];
      this.backendComplianceItems = [];
    }
  }

  private buildCredentialCheckboxes(): void {
    const activeKeys = new Set(this.activeRequirements.map(r => r.credential_type.key));

    this.backendReviewSources = this.allCredentialTypes
      .filter(t => t.category === 'external_link')
      .map(t => {
        const activeReq = this.activeRequirements.find(r => r.credential_type.key === t.key);
        return {
          key: t.key,
          label: t.key_label,
          checked: activeKeys.has(t.key),
          strictness: activeReq?.strictness || 'mandatory'
        };
      });

    this.backendComplianceItems = this.allCredentialTypes
      .filter(t => t.category !== 'external_link' && t.category !== 'core_profile')
      .map(t => {
        const activeReq = this.activeRequirements.find(r => r.credential_type.key === t.key);
        return {
          key: t.key,
          label: t.key_label,
          checked: activeKeys.has(t.key),
          strictness: activeReq?.strictness || 'mandatory'
        };
      });

    const backendReviewLabels = new Set(this.backendReviewSources.map(b => b.label));
    this.sourceNetworks = this.sourceNetworks.filter(s => !backendReviewLabels.has(s.value));

    const backendComplianceLabels = new Set(this.backendComplianceItems.map(b => b.label));
    this.complianceItems = this.complianceItems.filter(c => !backendComplianceLabels.has(c.value));
  }

  private async saveCredentialRequirements(): Promise<void> {
    const payload: SetRequirementPayload[] = [];
    for (const source of this.backendReviewSources) {
      if (source.checked) {
        payload.push({ credential_type_key: source.key, strictness: source.strictness });
      }
    }
    for (const item of this.backendComplianceItems) {
      if (item.checked) {
        payload.push({ credential_type_key: item.key, strictness: item.strictness });
      }
    }
    await firstValueFrom(
      this.credentialApi.setRequirements('address', payload, this.selectedAddressId)
    );
  }

  // These settings were never built, so they show the shared "reach out to enable this" prompt.
  gateLockedSetting = () => this.featureGate.gate();

}
